import React, { FC, memo, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import {
  ExpressCartSuggestion,
  useExpressCartSuggestions,
} from '../../../Providers/ExpressProductLibraryProvider';
import SuggestionCard from './SuggestionCard';

/** How long a dropped suggestion takes to fold away - matches CartLine's own exit. */
export const SUGGESTION_OUT_MS = 280;

/** Fades in when it (re)appears, so lines coming and going never make it pop. */
const Section = styled.section`
  min-width: 0;
  width: 100%;
  /* The last line brings 10px of its own. */
  margin-top: 2px;
  padding-top: 14px;
  border-top: 1px solid var(--eposBorderColor);
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: eposFadeIn 0.3s ease-out both;
  transition: opacity 0.25s ease-out;

  /* A line is being edited. !important outranks the fade-in's fill mode without restarting it. */
  .is-locked & {
    opacity: 0.3 !important;
    pointer-events: none;
  }

  @keyframes eposFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: -0.4px;
`;

/** Horizontal, one row, swipes sideways - never pushes the total off screen. */
const Rail = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding: 3px 3px 9px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

/**
 * One slot in the rail. A suggestion that drops out of the live list (its
 * parent line was removed, say) stays mounted here with `is-leaving` until it
 * has folded away, the same way a cart line does, instead of just vanishing.
 */
const CardSlot = styled.div`
  flex: 0 0 auto;
  width: var(--eposSuggestionCardWidth);
  overflow: hidden;
  transition: width ${SUGGESTION_OUT_MS}ms ease-in-out,
    margin-right ${SUGGESTION_OUT_MS}ms ease-in-out, opacity ${SUGGESTION_OUT_MS * 0.7}ms ease-in,
    transform ${SUGGESTION_OUT_MS * 0.7}ms ease-in;

  &.is-leaving {
    width: 0;
    /* Cancels the rail's gap so the slot fully collapses instead of leaving a gap-sized notch. */
    margin-right: -8px;
    opacity: 0;
    transform: scale(0.9);
    pointer-events: none;
  }
`;

interface DisplayedSuggestion {
  suggestion: ExpressCartSuggestion;
  leaving: boolean;
}

/**
 * "Anything else?" - multi-select upsells for the lines already in the cart,
 * as a swipeable row. Only this component subscribes to the suggestions
 * context, so the rest of the drawer is untouched when they change.
 */
const Suggestions: FC = () => {
  const { i18n } = useI18n();
  const suggestions = useExpressCartSuggestions();

  const [displayed, setDisplayed] = useState<DisplayedSuggestion[]>(() =>
    suggestions.map((suggestion) => ({ suggestion, leaving: false })),
  );
  const removalTimers = useRef(new Map<string, number>());

  // Suggestions gone from the live list are kept around, marked leaving, so
  // they can fold away instead of disappearing the instant they drop out.
  useEffect(() => {
    const liveKeys = new Set(suggestions.map((suggestion) => suggestion.key));

    setDisplayed((current) => {
      const next = current
        .filter((entry) => entry.leaving || liveKeys.has(entry.suggestion.key))
        .map((entry) =>
          liveKeys.has(entry.suggestion.key) ? entry : { ...entry, leaving: true },
        );

      suggestions.forEach((suggestion) => {
        if (!next.some((entry) => entry.suggestion.key === suggestion.key)) {
          next.push({ suggestion, leaving: false });
        }
      });

      return next;
    });
  }, [suggestions]);

  // Drops a leaving suggestion for good once its fold-away has played.
  useEffect(() => {
    displayed
      .filter((entry) => entry.leaving && !removalTimers.current.has(entry.suggestion.key))
      .forEach((entry) => {
        const key = entry.suggestion.key;
        removalTimers.current.set(
          key,
          window.setTimeout(() => {
            removalTimers.current.delete(key);
            setDisplayed((current) => current.filter((e) => e.suggestion.key !== key));
          }, SUGGESTION_OUT_MS),
        );
      });
  }, [displayed]);

  useEffect(() => {
    const timers = removalTimers.current;
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, []);

  if (displayed.length === 0) {
    return null;
  }

  return (
    <Section data-testid="epos-cart-suggestions">
      <Title>{i18n('Express.Cart.Suggestions')}</Title>
      <Rail>
        {displayed.map(({ suggestion, leaving }) => (
          <CardSlot key={suggestion.key} className={leaving ? 'is-leaving' : undefined}>
            <SuggestionCard suggestion={suggestion} />
          </CardSlot>
        ))}
      </Rail>
    </Section>
  );
};

export default memo(Suggestions);
